import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Grid, Theme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import * as InventoryActions from 'actions/InventoryActions';
import * as InventorySelectors from 'selectors/InventorySelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { push, Routes } from 'routing';

import { api } from 'common/frontend/api';
import { useQueryString } from 'common/hooks/useQueryString';
import { notUndefined } from 'common/utils/common';
import { useTranslation } from 'services/localization';
import * as ShopSelectors from 'selectors/ShopSelectors';

import Spinner from 'components/Spinner';
import DeleteArticlesDialog from 'components/DeleteArticlesDialog';

import InventorySearchBar from '../components/InventorySearchBar';
import RenameColumnDialog from './RenameColumnDialog';
import DeleteColumnDialog from './DeleteColumnDialog';
import ArticlesTable from './ArticlesTable/';
import { emptyInventoryFields, InventoryStatus } from 'common/modules/inventory';
import EmptyState from 'common/components/states/EmptyState';
import InventoryLearnMore from 'components/InventoryLearnMore';

const ArticlesView = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId);
	const [searchParams, setSearchParam] = useQueryString<{
		search: string;
		skuIds: string[];
		statuses: InventoryStatus[];
		locationIds: string[];
	}>({
		statuses: ['IN_USE', 'OUT_OF_USE'],
		locationIds: !!activeLocationId ? [activeLocationId] : undefined,
	});
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const inventoryItems = useSelector(InventorySelectors.inventoryItems);
	const skuItems = useSelector(InventorySelectors.skuItems);
	const inventoryConfiguration = useSelector(InventorySelectors.inventoryConfiguration);
	const [deleteItemsOpen, setDeleteItemsOpen] = useState<boolean>(false);
	const [deleteItemsLoading, setDeleteItemsLoading] = useState<boolean>(false);
	const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
	const [columnRenameId, setColumnRenameId] = useState<string | null>(null);
	const [columnDeleteId, setColumnDeleteId] = useState<string | null>(null);
	const userLocations = useSelector(UserSelectors.userLocations);
	const userLocationIds = userLocations.map(({ id }) => id);
	const userLocationsInventoryItems = (inventoryItems.data ?? []).filter(({ locationId }) =>
		userLocationIds.includes(locationId),
	);

	const selectedRowsCount = selectedRowIds.length;

	const deleteSelectedItems = async () => {
		setDeleteItemsLoading(true);
		const inventoryItemsToDelete = selectedRowIds
			.map((id) => {
				const inventoryItem = (inventoryItems.data ?? []).find((item) => item.id === id);
				return inventoryItem;
			})
			.filter(notUndefined);
		const massDelete = inventoryItemsToDelete.length > 50;
		if (massDelete) {
			dispatch(InventoryActions.clearInventoryItemsListener());
		}
		await Promise.all(
			inventoryItemsToDelete.map((item) => api().inventoryItems.doc(item.id).delete()),
		);
		if (massDelete) {
			dispatch(InventoryActions.setInventoryItemsListener(shopId));
		}
		setDeleteItemsOpen(false);
		setDeleteItemsLoading(false);
		setSelectedRowIds([]);
	};

	const handleSelectedLocationIdsChange = useCallback(
		(locationIds: string[]) => {
			setSearchParam('locationIds', locationIds);
		},
		[setSearchParam],
	);

	const handleSearchValueChange = useCallback(
		(search: string) => {
			setSearchParam('search', search);
		},
		[setSearchParam],
	);

	const handleSelectedSkuIdsChange = useCallback(
		(skuIds: string[]) => {
			setSearchParam('skuIds', skuIds);
		},
		[setSearchParam],
	);

	const handleStatusesChange = useCallback(
		(statuses: InventoryStatus[]) => {
			setSearchParam('statuses', statuses);
		},
		[setSearchParam],
	);

	if (!!inventoryItems.error) {
		return (
			<EmptyState
				title={t('common:errors.somethingWentWrongShort')}
				description={t('common:errors.pleaseReloadPage')}
			/>
		);
	}

	if (inventoryItems.loading) {
		return <Spinner />;
	}

	return (
		<>
			<Box className={classes.container}>
				<Grid container justifyContent="space-between" alignItems="center" spacing={4}>
					<Grid item className={classes.btnOrderWrapper}>
						{!!selectedRowsCount ? (
							<Button variant="contained" color="primary" onClick={() => setDeleteItemsOpen(true)}>
								{t('inventory.deleteSelected', 'Delete selected')} ({selectedRowsCount})
							</Button>
						) : (
							<Button
								variant="contained"
								color="primary"
								onClick={() => push(Routes.INVENTORY_ARTICLES_ADD)}
							>
								{t('inventory.articles.addArticles', 'Add articles')}
							</Button>
						)}
					</Grid>
					<Grid item xs={12} sm={true}>
						<InventorySearchBar
							searchValue={searchParams.search}
							onSearchValueChange={handleSearchValueChange}
							selectedLocationIds={searchParams.locationIds}
							onSelectedLocationIdsChange={handleSelectedLocationIdsChange}
							skuItems={skuItems}
							selectedSkuIds={searchParams.skuIds}
							onSelectedSkuIdsChange={handleSelectedSkuIdsChange}
							statuses={searchParams.statuses}
							onStatusesChange={handleStatusesChange}
						/>
					</Grid>
				</Grid>
				<Box mt={2} />
				<ArticlesTable
					inventoryFields={inventoryConfiguration.data?.fields ?? emptyInventoryFields()}
					inventoryItems={userLocationsInventoryItems}
					visibleSkuIds={searchParams.skuIds}
					selectedRowIds={selectedRowIds}
					setSelectedRowIds={setSelectedRowIds}
					searchValue={searchParams.search}
					statuses={searchParams.statuses}
					selectedLocationIds={searchParams.locationIds}
					openRenameColumnDialog={setColumnRenameId}
					openDeleteColumnDialog={setColumnDeleteId}
				/>
				<InventoryLearnMore />
			</Box>
			{deleteItemsOpen && (
				<DeleteArticlesDialog
					open={deleteItemsOpen}
					productCount={selectedRowsCount}
					onClose={() => setDeleteItemsOpen(false)}
					handleDelete={deleteSelectedItems}
					loading={deleteItemsLoading}
				/>
			)}
			{!!columnRenameId && (
				<RenameColumnDialog
					isOpen={!!columnRenameId}
					columnId={columnRenameId}
					onClose={() => setColumnRenameId(null)}
					inventoryFields={inventoryConfiguration.data?.fields ?? emptyInventoryFields()}
				/>
			)}
			{!!columnDeleteId && (
				<DeleteColumnDialog
					isOpen={!!columnDeleteId}
					columnId={columnDeleteId}
					onClose={() => setColumnDeleteId(null)}
					inventoryFields={inventoryConfiguration.data?.fields ?? emptyInventoryFields()}
				/>
			)}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		container: {
			flex: '1 1 auto',
			display: 'flex',
			flexDirection: 'column',
			padding: theme.spacing(4, 2, 7, 2),
			[theme.breakpoints.up('sm')]: {
				padding: theme.spacing(4, 4, 4, 4),
			},
			[theme.breakpoints.up('md')]: {
				padding: theme.spacing(4, 8, 4, 8),
			},
		},
		btnOrderWrapper: {
			order: 2,
			[theme.breakpoints.up('xs')]: {
				order: 1,
			},
		},
	}),
);

export default ArticlesView;
